import { useLocation, useNavigate } from 'react-router-dom';

// Supported languages
export const SUPPORTED_LANGUAGES = ['es', 'pl', 'sv'];
export const DEFAULT_LANGUAGE = 'en';

/**
 * Extract language from URL path
 * @param {string} pathname - Current pathname
 * @returns {string} - Language code (en, es, pl, sv)
 */
export const getLanguageFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (SUPPORTED_LANGUAGES.includes(firstSegment)) {
    return firstSegment;
  }
  
  return DEFAULT_LANGUAGE;
};

/**
 * Remove language prefix from pathname
 * @param {string} pathname - Current pathname
 * @returns {string} - Pathname without language prefix
 */
export const removeLanguagePrefix = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (SUPPORTED_LANGUAGES.includes(firstSegment)) {
    return '/' + segments.slice(1).join('/');
  }
  
  return pathname || '/';
};

/**
 * Add language prefix to path
 * @param {string} path - Path to add prefix to
 * @param {string} language - Language code
 * @returns {string} - Path with language prefix
 */
export const addLanguagePrefix = (path, language) => {
  // Remove any existing language prefix first
  const cleanPath = removeLanguagePrefix(path);
  
  // Don't add prefix for default language (English)
  if (language === DEFAULT_LANGUAGE) {
    return cleanPath;
  }
  
  // Add prefix for other languages
  return `/${language}${cleanPath === '/' ? '' : cleanPath}`;
};

/**
 * Custom hook for language-aware navigation
 * @returns {Function} - Navigate function that respects current language
 */
export const useLanguageNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = getLanguageFromPath(location.pathname);
  
  return (to, options = {}) => {
    const pathWithLang = addLanguagePrefix(to, currentLang);
    navigate(pathWithLang, options);
  };
};

/**
 * Get current language from URL
 * @returns {string} - Current language code
 */
export const useCurrentLanguage = () => {
  const location = useLocation();
  return getLanguageFromPath(location.pathname);
};
